<template>
	<V2Layout>
		<div class="v2-adv">

			<!-- Header -->
			<div class="v2-adv__header">
				<div class="v2-adv__header-left">
					<svg class="v2-adv__header-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.5"/>
						<path d="M4 20C4 16 7.5 13 12 13C16.5 13 20 16 20 20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
					</svg>
					<h1 class="v2-adv__title">Advertisers</h1>
					<span class="v2-adv__count">{{ totalCount }} total</span>
				</div>
				<div class="v2-adv__header-right">
					<router-link
						v-if="isDSP"
						:to="{ name: 'v2-advertiser-add' }"
						class="v2-adv__add-btn"
					>
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
						Add Advertiser
					</router-link>
				</div>
			</div>

			<!-- Filter Bar -->
			<div class="v2-adv__filters">
				<div class="v2-adv__pills">
					<button
						v-for="f in statusFilters"
						:key="f.value"
						class="v2-adv__pill"
						:class="{ 'v2-adv__pill--active': activeStatus === f.value }"
						@click="setStatus(f.value)"
					>
						{{ f.label }}
						<span v-if="f.count != null" class="v2-adv__pill-count">{{ f.count }}</span>
					</button>
				</div>

				<div class="v2-adv__filter-right">
					<div class="v2-adv__search">
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="1.3"/><path d="M9.5 9.5L13 13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
						<input
							v-model="searchQuery"
							type="text"
							placeholder="Search advertisers..."
							class="v2-adv__search-input"
							@input="onSearch"
						/>
					</div>

					<div class="v2-adv__view-toggle">
						<button
							class="v2-adv__view-btn"
							:class="{ 'v2-adv__view-btn--active': viewMode === 'cards' }"
							@click="viewMode = 'cards'"
							title="Card view"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.3"/><rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.3"/></svg>
						</button>
						<button
							class="v2-adv__view-btn"
							:class="{ 'v2-adv__view-btn--active': viewMode === 'table' }"
							@click="viewMode = 'table'"
							title="Table view"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4H14M2 8H14M2 12H14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
						</button>
					</div>
				</div>
			</div>

			<!-- Card View -->
			<div v-if="viewMode === 'cards'" class="v2-adv__cards">
				<div
					v-for="adv in advertisers"
					:key="adv.id"
					class="v2-adv-card"
					@click="editAdvertiser(adv.id)"
				>
					<div class="v2-adv-card__top">
						<span class="v2-adv-card__avatar" :style="{ background: avatarColor(adv.name) }">{{ (adv.name || '?')[0].toUpperCase() }}</span>
						<div class="v2-adv-card__name-block">
							<span class="v2-adv-card__name">{{ adv.name }}</span>
							<a v-if="adv.site" :href="adv.site" class="v2-adv-card__site" target="_blank" @click.stop>{{ adv.site.replace(/https?:\/\//, '') }} ↗</a>
						</div>
						<span class="v2-adv-card__status" :class="statusClass(adv)">{{ statusLabel(adv) }}</span>
					</div>

					<div class="v2-adv-card__info">
						<div v-if="adv.email" class="v2-adv-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="2.5" width="10" height="7" rx="1" stroke="currentColor" stroke-width="1.1"/><path d="M1 3.5L6 7L11 3.5" stroke="currentColor" stroke-width="1.1"/></svg>
							{{ adv.email }}
						</div>
						<div v-if="adv.phone" class="v2-adv-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 1.5H4.5L5.5 4L4 5C4.5 6.5 5.5 7.5 7 8L8 6.5L11 7.5V10C11 10.3 10.7 10.5 10.5 10.5C5.5 10.5 1.5 6.5 1.5 1.5C1.5 1.3 1.7 1 2 1" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
							{{ adv.phone }}
						</div>
						<div v-if="adv.contact_details" class="v2-adv-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="4" r="2" stroke="currentColor" stroke-width="1.1"/><path d="M2.5 10.5C2.5 8.5 4 7 6 7C8 7 9.5 8.5 9.5 10.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
							{{ adv.contact_details }}
						</div>
					</div>

					<div class="v2-adv-card__footer">
						<span class="v2-adv-card__agency">{{ agencyName(adv) }}</span>
						<span class="v2-adv-card__date">{{ formatDateTime(adv.ctime) }}</span>
					</div>
				</div>

				<div v-if="!advertisers.length" class="v2-adv__empty">
					<p>No advertisers found</p>
					<p class="v2-adv__empty-sub">Try adjusting your search or filters</p>
				</div>
			</div>

			<!-- Table View -->
			<div v-else class="v2-adv__table-wrap">
				<table class="nice-table">
					<thead>
						<tr>
							<th>{{ $t('advertiser.advertiser_column_label') }}&nbsp;&mdash;</th>
							<th>{{ $t('advertiser.status_column_label') }}&nbsp;&mdash;</th>
							<th>{{ $t('advertiser.phone_number_column_label') }}&nbsp;&mdash;</th>
							<th>{{ $t('advertiser.contact_column_label') }}&nbsp;&mdash;</th>
							<th>{{ $t('advertiser.email_column_label') }}&nbsp;&mdash;</th>
							<th>{{ $t('advertiser.company_website_label') }}&nbsp;&mdash;</th>
							<th>{{ $t('advertiser.created_column_label') }}&nbsp;&mdash;</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="adv in advertisers" :key="adv.id" class="v2-adv__row" @click="editAdvertiser(adv.id)">
							<td class="v2-adv__row-name">{{ adv.name }}</td>
							<td><span class="v2-adv-card__status" :class="statusClass(adv)">{{ statusLabel(adv) }}</span></td>
							<td>{{ adv.phone }}</td>
							<td>{{ adv.contact_details }}</td>
							<td>{{ adv.email }}</td>
							<td>{{ adv.site }}</td>
							<td>{{ formatDateTime(adv.ctime) }}</td>
						</tr>
						<tr v-if="!advertisers.length"><td colspan="7" class="v2-adv__empty-cell">No advertisers found</td></tr>
					</tbody>
				</table>
			</div>

			<!-- Pagination -->
			<div class="v2-adv__pagination">
				<span class="v2-adv__pagination-info">Showing {{ paginationFrom }}–{{ paginationTo }} of {{ totalCount }}</span>
				<div class="v2-adv__pagination-right">
					<span class="v2-adv__pagination-label">Lines per page</span>
					<select v-model.number="pageSize" class="v2-adv__pagination-select" @change="loadPage(1)">
						<option :value="20">20</option>
						<option :value="50">50</option>
						<option :value="100">100</option>
					</select>
					<div class="v2-adv__pagination-pages">
						<button class="v2-adv__page-btn" :disabled="page <= 1" @click="loadPage(page - 1)">&lt;</button>
						<button v-for="p in visiblePages" :key="p" class="v2-adv__page-btn" :class="{ 'v2-adv__page-btn--active': p === page }" @click="loadPage(p)">{{ p }}</button>
						<button class="v2-adv__page-btn" :disabled="page >= totalPages" @click="loadPage(page + 1)">&gt;</button>
					</div>
				</div>
			</div>

			<router-view @close-add-advertiser="requestAdvertisers" />
		</div>
	</V2Layout>
</template>


<script>
import { mapState, mapGetters } from 'vuex';
import V2Layout from '@/layouts/V2Layout';
import { profileFormatDateTime as formatDateTime } from '@/utilites';
import advertiserApi from '@/api/advertiser.js';
import { STATUS_AWAITING_APPROVAL, STATUS_APPROVED } from '@/constants/advertiser.js';

export default {
	name: 'V2AdvertiserList',
	components: { V2Layout },

	data() {
		return {
			advertisers: [],
			totalCount: 0,
			page: 1,
			pageSize: 20,
			searchQuery: '',
			activeStatus: '---',
			viewMode: localStorage.getItem('v2AdvViewMode') || 'cards',
			searchTimeout: null,
		};
	},

	async mounted() {
		await this.requestAdvertisers();
	},

	watch: {
		viewMode(val) { localStorage.setItem('v2AdvViewMode', val); },
	},

	computed: {
		...mapState('app', { appSide: 'side' }),
		...mapGetters('app', ['isDSP']),

		statusFilters() {
			return [
				{ value: '---', label: 'All', count: this.totalCount },
				{ value: STATUS_AWAITING_APPROVAL, label: 'Awaiting', count: null },
				{ value: STATUS_APPROVED, label: 'Approved', count: null },
			];
		},

		totalPages() { return Math.ceil(this.totalCount / this.pageSize) || 1; },
		paginationFrom() { return this.totalCount ? (this.page - 1) * this.pageSize + 1 : 0; },
		paginationTo() { return Math.min(this.page * this.pageSize, this.totalCount); },

		visiblePages() {
			const pages = [];
			for (let i = Math.max(1, this.page - 2); i <= Math.min(this.totalPages, this.page + 2); i++) pages.push(i);
			return pages;
		},
	},

	methods: {
		formatDateTime,

		async requestAdvertisers() {
			let loading = this.$loading.show();
			try {
				const filters = {};
				if (this.searchQuery) filters.search = this.searchQuery;
				if (this.activeStatus !== '---') filters.status = this.activeStatus;
				const res = await advertiserApi.list(filters, this.page, this.pageSize);
				this.advertisers = res.results || [];
				this.totalCount = res.count || 0;
			} catch (err) { this.$log.error(err); }
			finally { loading.hide(); }
		},

		setStatus(s) { this.activeStatus = s; this.page = 1; this.requestAdvertisers(); },

		onSearch() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => { this.page = 1; this.requestAdvertisers(); }, 300);
		},

		loadPage(p) { if (p >= 1 && p <= this.totalPages) { this.page = p; this.requestAdvertisers(); } },

		editAdvertiser(id) {
			if (!this.isDSP) {
				this.$router.push({ name: 'v2-advertiser-edit', params: { advertiserId: id } });
			}
		},

		statusLabel(adv) {
			const s = this.isDSP
				? (adv.ssp_agencies_instances && adv.ssp_agencies_instances[0] ? adv.ssp_agencies_instances[0].status : null)
				: (adv.dsp_agency_instance ? adv.dsp_agency_instance.status : null);
			if (s === STATUS_APPROVED) return 'Approved';
			if (s === STATUS_AWAITING_APPROVAL) return 'Awaiting';
			return s || '';
		},

		statusClass(adv) {
			const l = this.statusLabel(adv);
			return l === 'Approved' ? 'v2-adv-card__status--approved' : l === 'Awaiting' ? 'v2-adv-card__status--awaiting' : '';
		},

		agencyName(adv) {
			if (this.isDSP && adv.ssp_agencies_instances && adv.ssp_agencies_instances[0]) return adv.ssp_agencies_instances[0].name;
			return adv.dsp_agency_instance ? adv.dsp_agency_instance.name : '';
		},

		avatarColor(name) {
			const c = ['#2a9d8f','#7c5ecf','#2563eb','#f97316','#22c55e','#ef4444','#0891b2','#eab308'];
			return c[((name||'').charCodeAt(0)||0) % c.length];
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

.v2-adv
	font-family: $v2-font

.v2-adv__header
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 20px

.v2-adv__header-left
	display: flex
	align-items: center
	gap: 10px

.v2-adv__header-icon
	color: $v2-text-muted

.v2-adv__title
	font-size: 22px
	font-weight: 700
	margin: 0
	color: $v2-text

.v2-adv__count
	font-size: 12px
	color: $v2-text-muted
	background: #f3f4f6
	padding: 3px 10px
	border-radius: 20px

.v2-adv__add-btn
	display: inline-flex
	align-items: center
	gap: 8px
	padding: 9px 20px
	background: $v2-teal
	color: #fff
	border: none
	border-radius: 10px
	font-family: $v2-font
	font-size: 13px
	font-weight: 600
	text-decoration: none
	transition: all 0.15s

	&:hover
		background: darken($v2-teal, 8%)

.v2-adv__filters
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 20px
	gap: 16px
	flex-wrap: wrap

.v2-adv__pills
	display: flex
	gap: 6px

.v2-adv__pill
	display: inline-flex
	align-items: center
	gap: 4px
	padding: 6px 14px
	border: 1.5px solid $v2-border
	border-radius: 20px
	background: transparent
	font-family: $v2-font
	font-size: 12px
	font-weight: 500
	color: $v2-text-muted
	cursor: pointer
	transition: all 0.15s

	&:hover
		border-color: $v2-teal
		color: $v2-teal

	&--active
		background: rgba($v2-teal, 0.08)
		border-color: $v2-teal
		color: $v2-teal
		font-weight: 600

.v2-adv__pill-count
	font-size: 11px
	opacity: 0.7

.v2-adv__filter-right
	display: flex
	align-items: center
	gap: 10px

.v2-adv__search
	display: flex
	align-items: center
	gap: 8px
	padding: 7px 14px
	background: $v2-card-bg
	border: 1px solid $v2-border
	border-radius: 10px
	min-width: 220px
	color: $v2-text-muted

.v2-adv__search-input
	border: none !important
	outline: none !important
	background: transparent !important
	font-family: $v2-font
	font-size: 13px
	color: $v2-text
	width: 100%
	padding: 0 !important
	min-height: 0 !important
	box-shadow: none !important

	&::placeholder
		color: $v2-text-label

.v2-adv__view-toggle
	display: flex
	background: #f3f4f6
	border-radius: 8px
	padding: 2px

.v2-adv__view-btn
	display: flex
	align-items: center
	justify-content: center
	width: 32px
	height: 32px
	border: none
	background: transparent
	border-radius: 6px
	cursor: pointer
	color: $v2-text-muted
	transition: all 0.15s

	&:hover
		color: $v2-text

	&--active
		background: $v2-card-bg
		color: $v2-text
		box-shadow: 0 1px 3px rgba(0,0,0,0.08)

.v2-adv__cards
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 14px

	@media (max-width: 1024px)
		grid-template-columns: repeat(2, 1fr)

	@media (max-width: 768px)
		grid-template-columns: 1fr

.v2-adv-card
	background: $v2-card-bg
	border: 1px solid $v2-border
	border-radius: $v2-radius-card
	padding: 18px 20px
	cursor: pointer
	transition: all 0.2s
	display: flex
	flex-direction: column

	&:hover
		box-shadow: $v2-shadow-hover
		transform: translateY(-2px)

.v2-adv-card__top
	display: flex
	align-items: flex-start
	gap: 10px
	margin-bottom: 14px

.v2-adv-card__avatar
	width: 36px
	height: 36px
	border-radius: 50%
	display: flex
	align-items: center
	justify-content: center
	color: #fff
	font-weight: 700
	font-size: 15px
	flex-shrink: 0

.v2-adv-card__name-block
	flex: 1
	min-width: 0

.v2-adv-card__name
	font-size: 14px
	font-weight: 600
	color: $v2-text
	display: block

.v2-adv-card__site
	font-size: 11px
	color: $v2-teal
	text-decoration: none

	&:hover
		text-decoration: underline

.v2-adv-card__status
	font-size: 11px
	font-weight: 600
	padding: 3px 10px
	border-radius: 20px
	white-space: nowrap
	flex-shrink: 0

	&--approved
		background: rgba($v2-green, 0.1)
		color: darken($v2-green, 10%)

	&--awaiting
		background: rgba(#eab308, 0.1)
		color: #a16207

.v2-adv-card__info
	display: flex
	flex-direction: column
	gap: 6px
	margin-bottom: 14px
	flex: 1

.v2-adv-card__row
	display: flex
	align-items: center
	gap: 8px
	font-size: 12px
	color: $v2-text-muted

	svg
		flex-shrink: 0
		color: $v2-text-label

.v2-adv-card__footer
	display: flex
	justify-content: space-between
	align-items: center
	padding-top: 12px
	border-top: 1px solid $v2-border

.v2-adv-card__agency
	font-size: 11px
	color: $v2-text-muted

.v2-adv-card__date
	font-size: 11px
	color: $v2-text-label

.v2-adv__table-wrap
	overflow-x: auto

.v2-adv__row
	cursor: pointer

.v2-adv__row-name
	font-weight: 600

.v2-adv__empty-cell
	text-align: center
	padding: 40px !important
	color: $v2-text-muted

.v2-adv__empty
	grid-column: 1 / -1
	text-align: center
	padding: 60px 20px
	color: $v2-text-muted

.v2-adv__empty-sub
	font-size: 13px
	color: $v2-text-label

.v2-adv__pagination
	display: flex
	align-items: center
	justify-content: space-between
	margin-top: 20px

.v2-adv__pagination-info
	font-size: 13px
	color: $v2-text-muted

.v2-adv__pagination-right
	display: flex
	align-items: center
	gap: 12px

.v2-adv__pagination-label
	font-size: 12px
	color: $v2-text-muted

.v2-adv__pagination-select
	padding: 5px 8px
	border: 1px solid $v2-border
	border-radius: 8px
	font-family: $v2-font
	font-size: 12px
	background: $v2-card-bg
	color: $v2-text

.v2-adv__pagination-pages
	display: flex
	gap: 4px

.v2-adv__page-btn
	width: 32px
	height: 32px
	display: flex
	align-items: center
	justify-content: center
	border: 1px solid $v2-border
	border-radius: 8px
	background: $v2-card-bg
	font-family: $v2-font
	font-size: 12px
	font-weight: 500
	color: $v2-text-muted
	cursor: pointer
	transition: all 0.15s

	&:hover:not(:disabled)
		border-color: $v2-teal
		color: $v2-teal

	&:disabled
		opacity: 0.4
		cursor: default

	&--active
		background: $v2-teal
		border-color: $v2-teal
		color: #fff
</style>
